import { IconButton, Checkbox, Box, Typography, TextField } from '@mui/material';
import { CopyAll as CopyAllIcon, DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import editIcon from "../../assets/images/tarrifManagement/edit.png";
import moment from "moment";
import { formatTimeFromSeconds } from "../../utils/payoutUtils";
import { getRideTypeLabel } from "../../Constant/serviceTypeConstants";

/**
 * Configuration for the expandable payout detail fields.
 * Each entry defines a label and an accessor that extracts the display value
 * from the payout object.
 */
export const PAYOUT_DETAIL_FIELDS = [
    { label: "Initial Distance", accessor: (payout) => payout.initialDistance ?? "-" },
    { label: "Initial Distance Charge", accessor: (payout) => payout.initialDistanceCharge ?? "-" },
    { label: "Per Km Charge", accessor: (payout) => payout.perKmCharge ?? "-" },
    { label: "Time From", accessor: (payout) => formatTimeFromSeconds(payout.timeFrom) },
    { label: "Time To", accessor: (payout) => formatTimeFromSeconds(payout.timeTo) },
];

/**
 * Get column definitions for Payout table (DataGrid format)
 * @param {Function} onEdit - Callback when edit button is clicked
 */
export const getPayoutColumns = (onEdit) => [
    {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fontColor: '#39B54A',
        fontWeight: 600,
    },
    {
        field: 'serviceCityName',
        headerName: 'Service City',
        flex: 1,
        minWidth: 120,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
    },
    {
        field: 'initialDistance',
        headerName: 'Initial Distance',
        flex: 1,
        minWidth: 130,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fontWeight: 400
    },
    {
        field: 'initialDistanceCharge',
        headerName: 'Initial Distance Charge',
        flex: 1,
        minWidth: 160,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fontWeight: 400
    },
    {
        field: 'perKmCharge',
        headerName: 'Per KM Charge',
        flex: 1,
        minWidth: 120,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fontWeight: 400
    },
    {
        field: 'timeFrom',
        headerName: 'Time From',
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (params.value !== null && params.value !== undefined) ? moment().startOf('day').add(params.value, 'seconds').format('h:mm A') : '-',
    },
    {
        field: 'timeTo',
        headerName: 'Time To',
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (params.value !== null && params.value !== undefined) ? moment().startOf('day').add(params.value, 'seconds').format('h:mm A') : '-',
    },
    {
        field: 'carTypeName',
        headerName: 'Car Type',
        flex: 1,
        minWidth: 100,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fullWidth: true,
    },
    {
        field: 'id',
        headerName: 'Action',
        flex: 0.5,
        minWidth: 100,
        renderCell: (params) => (
            <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(params.row); }}
            >
                <img
                    src={editIcon}
                    alt="edit"
                    style={{
                        width: 20,
                        height: 20,
                        filter: "brightness(0) invert(0.4)"
                    }}
                />

            </IconButton>
        )
    }

];

export const getMappingColumns = (onEdit, onDelete) => [
    {
        field: 'companyName',
        headerName: 'Client Name',
        flex: 1,
        minWidth: 150,
        sortable: true,
        fontWeight: 600,
        fontColor: '#39B54A',
        renderCell: (params) => params.value || '-',
    },
    {
        field: 'packageName',
        headerName: 'Package Name',
        flex: 1,
        minWidth: 100,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fullWidth: true,
        renderCell: (params) => params.value || '-',
    },
    {
        field: 'rideType',
        headerName: 'Ride Type',
        flex: 1,
        minWidth: 100,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fullWidth: true,
        renderCell: (params) => getRideTypeLabel(params.value),
    },
    {
        field: 'carTypeName',
        headerName: 'Car Type',
        flex: 1,
        minWidth: 100,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        fullWidth: true,
        renderCell: (params) => params.row.driverPayout?.carTypeName || '-',
    },
    {
        field: 'driverPayout',
        headerName: 'Payout Name',
        flex: 1,
        minWidth: 150,
        sortable: true,
        fontWeight: 400,
        renderCell: (params) => params.value?.name || '-',
    },
    {
        field: 'percentage',
        headerName: 'Deduction Percentage',
        flex: 0.5,
        minWidth: 100,
        sortable: true,
        fontWeight: 600,
        renderCell: (params) => params.value ? `${params.value}%` : '-',
    },
    {
        field: 'id',
        headerName: 'Action',
        flex: 0.7,
        minWidth: 120,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onEdit(params.row); }}
                >
                    <img
                        src={editIcon}
                        alt="edit"
                        style={{ width: 20, height: 20, filter: "brightness(0) invert(0.4)" }}
                    />
                </IconButton>
                {onDelete && (
                    <IconButton
                        size="medium"
                        onClick={(e) => { e.stopPropagation(); onDelete(params.row); }}
                        sx={{ color: '#D32F2F' }}
                    >
                        <DeleteOutlineIcon fontSize="medium" />
                    </IconButton>
                )}
            </Box>
        )
    }
];

/**
 * Get column definitions for Package Selection table with checkboxes and percentage fields
 * @param {Array} selectedPackages - Array of selected packages [{id, percentage}, ...]
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Function} onPercentageChange - Callback when percentage changes (packageId, value)
 * @param {Function} onApplyToAll - Callback when apply to all is clicked (packageId)
 * @param {Object} percentageErrors - Validation errors {[packageId]: errorMessage}
 */
export const getPackageSelectionColumns = (selectedPackages, onSelectionChange, onPercentageChange, onApplyToAll, percentageErrors = {}) => [
    {
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: (params) => {
            const isSelected = selectedPackages.some(pkg => pkg.id === params.row.id);
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                        const newSelection = e.target.checked
                            ? [...selectedPackages, { id: params.row.id, percentage: "" }]
                            : selectedPackages.filter(pkg => pkg.id !== params.row.id);
                        onSelectionChange(newSelection);
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        },
    },
    {
        field: 'packageName',
        headerName: 'Package Name',
        flex: 1.5,
        minWidth: 200,
        sortable: true,
        fontColor: '#39B54A',
        fontWeight: 600,
    },
    {
        field: 'serviceCityName',
        headerName: 'Service City',
        flex: 1,
        minWidth: 130,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
    },
    {
        field: 'isActive',
        headerName: 'Available',
        flex: 1,
        minWidth: 120,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.value ? (
            <Box
                sx={{
                    display: "flex",
                    backgroundColor: "#E3F5E1",
                    width: "6.11vw",
                    height: "2.01vw",
                    justifyContent: "space-around",
                    alignItems: "center",
                    borderRadius: "6px",
                    padding: "6px",
                    border: "1px solid rgba(116, 206, 106, 1)",
                }}
            >
                <Box
                    sx={{
                        width: "0.56vw",
                        height: "0.56vw",
                        background: "rgba(116, 206, 106, 1)",
                        borderRadius: "50%",
                    }}
                />
                <Typography sx={{ fontSize: "0.97vw" }}>
                    Available
                </Typography>
            </Box>
        ) : (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    width: "7vw",
                    height: "2.01vw",
                    borderRadius: "6px",
                    padding: "6px",
                    backgroundColor: "#ffe5e5", // light red
                    border: "1px solid #cc0000", // darker red border
                }}
            >
                <Box
                    sx={{
                        width: "0.56vw",
                        height: "0.56vw",
                        backgroundColor: "#cc0000",
                        borderRadius: "50%",
                    }}
                />
                <Typography
                    sx={{
                        fontSize: "0.97vw",
                        color: "#cc0000",
                    }}
                >
                    Unavailable
                </Typography>
            </Box>
        )
    },
    {
        field: 'percentage',
        headerName: 'Percentage (%)',
        flex: 1.5,
        minWidth: 250,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
            const selectedPackage = selectedPackages.find(pkg => pkg.id === params.row.id);
            const isSelected = !!selectedPackage;
            const hasError = percentageErrors[params.row.id];

            return (
                <TextField
                    type="number"
                    fullWidth
                    value={selectedPackage?.percentage || ""}
                    onChange={(e) => {
                        if (e.target.value.length <= 3) {
                            onPercentageChange(params.row.id, e.target.value);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                    placeholder="0"
                    size="small"
                    disabled={!isSelected}
                    inputProps={{ min: 0, max: 100 }}
                    error={!!hasError}
                    helperText={hasError}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#39B54A',
                            },
                        },
                        minWidth: "120px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        },
    },
    {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
            const selectedPackage = selectedPackages.find(pkg => pkg.id === params.row.id);
            const isSelected = !!selectedPackage;
            const hasPercentage = selectedPackage?.percentage !== "" && selectedPackage?.percentage !== undefined;
            const hasError = !!percentageErrors[params.row.id];

            return (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onApplyToAll(params.row.id);
                    }}
                    disabled={!isSelected || !hasPercentage || selectedPackages.length === 1 || hasError}
                    title="Apply this percentage to all selected packages"
                    sx={{
                        color: '#2196f3',
                        '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.08)' },
                        '&:disabled': { color: '#bdbdbd' },
                    }}
                >
                    <CopyAllIcon fontSize="small" />
                </IconButton>
            );
        },
    }
];
